import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type NotificationView } from "../api/client";
import { Avatar } from "./Avatar";
import { OverflowMenu } from "./OverflowMenu";
import { formatRelativeTime } from "../util/date";

/**
 * 30s, not the 10s the board polls at — a mention isn't urgent enough to add to
 * that cadence, and the bell is mounted on every authenticated page.
 */
const POLL_MS = 30_000;

function describe(n: NotificationView): string {
  switch (n.type) {
    case "mention":
      return "mentioned you in";
    case "assigned":
      return "assigned you to";
    default:
      return "acted on";
  }
}

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // The list is the source of truth for the badge too — one request, not two.
  // (unread_count exists for the API/MCP side, where nobody wants the payload.)
  const { data: items = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    refetchInterval: POLL_MS,
  });

  const unread = items.filter((n) => !n.readAt);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: invalidate,
  });
  const markAllRead = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: invalidate,
  });

  function open(n: NotificationView, close: () => void) {
    if (!n.readAt) markRead.mutate(n.id);
    close();
    navigate(`/boards/${n.boardId}/cards/${n.cardId}`);
  }

  return (
    <OverflowMenu
      ariaLabel={
        unread.length ? `Notifications (${unread.length} unread)` : "Notifications"
      }
      align="right"
      triggerClassName="relative rounded p-1.5 leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      trigger={
        <>
          <Bell size={18} />
          {unread.length > 0 && (
            <span
              // A count over 9 would widen the badge past the bell; cap the label.
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium text-white"
            >
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </>
      }
    >
      {(close) => (
        <div className="w-80">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-sm font-medium text-slate-700">
              Notifications
            </span>
            {unread.length > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="text-xs text-blue-600 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="border-t border-slate-100" />

          {items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-500">
              Nothing yet. You'll be notified when someone @mentions you.
            </p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => open(n, close)}
                    className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50 ${
                      n.readAt ? "" : "bg-blue-50/60"
                    }`}
                  >
                    <Avatar
                      userId={n.actorId ?? "system"}
                      name={n.actorDisplayName ?? n.actorUsername ?? "?"}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-600">
                        <span className="font-medium text-slate-800">
                          {n.actorDisplayName ?? n.actorUsername ?? "Someone"}
                        </span>{" "}
                        {describe(n)}{" "}
                        <span className="font-medium text-slate-800">
                          {n.cardTitle}
                        </span>
                      </div>
                      {n.data?.excerpt && (
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {n.data.excerpt}
                        </div>
                      )}
                      <div className="mt-0.5 text-xs text-slate-400">
                        {formatRelativeTime(n.createdAt)}
                      </div>
                    </div>
                    {!n.readAt && (
                      <span
                        aria-hidden
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </OverflowMenu>
  );
}
